import { useState } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
} from "@material-ui/core";

import { TextButton, Select, TransparentTextInput, AddressAutoComplete } from "../base";

import { COUNTRIES } from "./constants";
import { ContextMenu } from "../../components/contextMenu";
import trashIcon from "../../assets/images/icons/trash.svg";
import { copyObject, extractAddressInfo } from "../../services/utils";
import { COLORS } from "../../theme/colors";
import { AREA_TYPES, BUILDING_SEGMENTS } from "../../constants";

const useStyles = makeStyles((theme) => ({
  lightPurple: {
    backgroundColor: COLORS.purples[20],
  },
  table: {
    maxHeight: "60vh",
    marginTop: theme.spacing(1.75),
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(33, 33, 33, 0.04)",
    },
    "&:focus": {
      backgroundColor: COLORS.black,
      opacity: 0.1,
    },
  },
  trashIcon: {
    position: "absolute",
    display: "none",
    cursor: "pointer",
    width: 14,
  },
  addRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  error: {
    backgroundColor: COLORS.lightPink,
  },
}));

const HEADERS = [
  {},
  { key: "name", text: "Building name" },
  { key: "address", text: "Building address" },
  { key: "country", text: "Country" },
  { key: "zip_code", text: "Zip" },
  { key: "total_square", text: "Total square" },
  { key: "roof_square", text: "Roof square" },
  { key: "area_type", text: "Area type" },
  { key: "segment", text: "Type" },
  { key: "total_unit", text: "Units" },
  { key: "total_tenants", text: "Tenants" },
  { key: "avg_electricity_price", text: "Avg electricity price" },
  { key: "consumption_monthly", text: "Consumption monthly" },
  { key: "consumption_yearly", text: "Consumption yearly" },
];

const DEFAULT_ROW = {};

export const PortfolioTable = ({ rows, setRows, disableTable, rowsErrors, deleteBuilding }) => {
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [clickedRow, setClickedRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  const [sortColumns, setSortColumns] = useState({});
  const classes = useStyles();

  const onCellDataChange = (rowIndex, key, value) => {
    if (typeof value === "string") {
      value = value.trimStart();
      if (!isNaN(value)) {
        value = parseFloat(value);
      }
    }
    setRows((prev) => {
      const newRows = copyObject(prev);
      newRows[rowIndex][key] = value;
      return newRows;
    });
  };

  const addRowAbove = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow && copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const addRowBelow = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex + 1);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex + 1)));
    if (copiedRow && copiedRow > rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const deleteRow = (rowIndex) => {
    setRows(rows.slice(0, rowIndex).concat(rows.slice(rowIndex + 1)));
    if (copiedRow > rowIndex) {
      setCopiedRow(copiedRow - 1);
    } else if (copiedRow === rowIndex) {
      setCopiedRow(null);
    }
  };

  const copyRow = (rowIndex) => {
    setCopiedRow(rowIndex);
  };

  const pasteRow = (rowIndex) => {
    if (copiedRow === null) return;
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(rows[copiedRow]);
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const sortByColumn = (name) => {
    const columns = {};
    if (!sortColumns[name]) {
      columns[name] = "asc";
    } else if (sortColumns[name] === "asc") {
      columns[name] = "desc";
    } else {
      columns[name] = "asc";
    }
    setSortColumns(columns);
    setRows((r) => {
      const emptyRows = [];
      const rows = [];
      r.forEach((obj) => {
        if (!!obj[name]) {
          rows.push(obj);
        } else {
          emptyRows.push(obj);
        }
      });
      rows.sort((a, b) => {
        if (a[name] > b[name]) {
          return 1;
        } else if (a[name] < b[name]) {
          return -1;
        }
        return 0;
      });
      if (columns[name] === "asc") return rows.concat(emptyRows);
      return rows.reverse().concat(emptyRows);
    });
  };

  const addNewRow = () => {
    setRows((r) => {
      const newRows = copyObject(r);
      newRows.push(copyObject(DEFAULT_ROW));
      return newRows;
    });
  };

  const contextMenuItems = [
    { text: "Add row above", onClick: addRowAbove },
    { text: "Add row below", onClick: addRowBelow },
    { text: "Delete row", onClick: deleteRow },
    { text: "Copy", onClick: copyRow },
    { text: "Paste", onClick: pasteRow },
  ];

  const getSegment = (segment) => {
    return segment?.toUpperCase().replace("-", "_");
  };

  const checkCellError = (name, i) => {
    return rowsErrors[i] && name in rowsErrors[i] ? classes.error : null;
  };

  const handleSelected = (address, i) => {
    const { country, postalCode } = extractAddressInfo(address);
    onCellDataChange(i, "country", country);
    onCellDataChange(i, "zip_code", postalCode);
  };

  return (
    <>
      <TableContainer className={classes.table}>
        <Table stickyHeader style={{ height: "100%" }}>
          <TableHead>
            <TableRow>
              {HEADERS.map((header, index) => (
                <TableCell key={index} style={{ height: "40px", fontWeight: 600 }}>
                  <TableSortLabel
                    style={{ width: header.key === "address" ? 300 : "fit-content" }}
                    active={!!sortColumns[header.key]}
                    direction={sortColumns[header.key] || "asc"}
                    onClick={() => sortByColumn(header.key)}
                  >
                    <Typography style={{ whiteSpace: "nowrap" }}>{header.text}</Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                className={classes.row}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (!disableTable) {
                    setRightClickEvent(event);
                    setClickedRow(index);
                  }
                }}
                key={index}
              >
                <TableCell width={160}>
                  <img
                    onClick={() => {
                      if (!disableTable) {
                        deleteRow(index);
                        if (row?.id) deleteBuilding(row.id);
                      }
                    }}
                    src={trashIcon}
                    alt="trash-icon"
                    style={{ paddingLeft: 2, paddingTop: 6 }}
                  />
                </TableCell>
                <TableCell
                  width={160}
                  className={`${row.name ? null : classes.lightPurple} ${checkCellError(
                    "name",
                    index
                  )}`}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    value={row.name}
                    onChange={(v) => onCellDataChange(index, "name", v)}
                  />
                </TableCell>
                <TableCell
                  width={300}
                  className={`
                    ${row.address ? null : classes.lightPurple}
                    ${checkCellError("address", index)}
                  `}
                >
                  <AddressAutoComplete
                    disabled={disableTable}
                    value={row.address}
                    onSeleted={(address) => {
                      handleSelected(address, index);
                      onCellDataChange(index, "address", address.formatted_address);
                    }}
                    onChange={(v) => onCellDataChange(index, "address", v)}
                  />
                </TableCell>
                <TableCell
                  width={80}
                  className={`
                    ${row.country ? null : classes.lightPurple}
                    ${checkCellError("country", index)}
                  `}
                >
                  <Select
                    disabled={disableTable}
                    options={COUNTRIES}
                    value={row.country ? row.country : ""}
                    onChange={(v) => {
                      onCellDataChange(index, "country", v);
                      onCellDataChange(index, "address", "");
                      onCellDataChange(index, "zip_code", "");
                    }}
                  />
                </TableCell>
                <TableCell
                  width={80}
                  className={`
                    ${row.zip_code ? null : classes.lightPurple}
                    ${checkCellError("zip_code", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    value={row.zip_code}
                    onChange={(v) => onCellDataChange(index, "zip_code", v)}
                  />
                </TableCell>
                <TableCell
                  width={80}
                  className={`
                    ${row.total_square ? null : classes.lightPurple}
                    ${checkCellError("total_square", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.total_square}
                    onChange={(v) => onCellDataChange(index, "total_square", v)}
                  />
                </TableCell>
                <TableCell
                  width={80}
                  className={`
                    ${row.roof_square ? null : classes.lightPurple}
                    ${checkCellError("roof_square", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.roof_square}
                    onChange={(v) => onCellDataChange(index, "roof_square", v)}
                  />
                </TableCell>
                <TableCell className={checkCellError("area_type", index)} width={1}>
                  <Select
                    disabled={disableTable}
                    options={AREA_TYPES}
                    value={row.area_type?.toUpperCase()}
                    onChange={(v) => onCellDataChange(index, "area_type", v)}
                  />
                </TableCell>
                <TableCell className={checkCellError("segment", index)} width={1}>
                  <Select
                    disabled={disableTable}
                    options={BUILDING_SEGMENTS}
                    value={getSegment(row.segment)}
                    onChange={(v) => onCellDataChange(index, "segment", v)}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={`
                      ${row.total_unit ? null : classes.lightPurple}
                      ${checkCellError("total_unit", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.total_unit}
                    onChange={(v) => onCellDataChange(index, "total_unit", v)}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={`
                    ${row.total_tenants ? null : classes.lightPurple}
                    ${checkCellError("total_tenants", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.total_tenants}
                    onChange={(v) => onCellDataChange(index, "total_tenants", v)}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={`
                    ${row.avg_electricity_price_currency_corrected ? null : classes.lightPurple}
                    ${checkCellError("avg_electricity_price", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.avg_electricity_price_currency_corrected}
                    onChange={(v) => onCellDataChange(index, "avg_electricity_price", v)}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={`
                    ${row.consumption_monthly ? null : classes.lightPurple}
                    ${checkCellError("consumption_monthly", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.consumption_monthly}
                    onChange={(v) => onCellDataChange(index, "consumption_monthly", v)}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={`
                    ${row.consumption_yearly ? null : classes.lightPurple}
                    ${checkCellError("consumption_yearly", index)}
                  `}
                >
                  <TransparentTextInput
                    disabled={disableTable}
                    type="number"
                    value={row.consumption_yearly}
                    onChange={(v) => onCellDataChange(index, "consumption_yearly", v)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ContextMenu
        items={contextMenuItems}
        event={rightClickEvent}
        onClose={() => {
          setRightClickEvent(null);
          setClickedRow(null);
        }}
        clickedRow={clickedRow}
        copiedRow={copiedRow}
      />
      {!disableTable && (
        <Box className={classes.addRow}>
          <TextButton text="Add row" onClick={addNewRow} />
        </Box>
      )}
    </>
  );
};
